import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecipientTable = ({ recipients, onEdit, onDelete, onSendMoney, onViewDetails }) => {
  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'failed':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'AlertCircle';
    }
  };

  const formatLastUsed = (date) => {
    const now = new Date();
    const lastUsed = new Date(date);
    const diffInDays = Math.floor((now - lastUsed) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return lastUsed?.toLocaleDateString('en-GB');
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Recipient</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Bank Details</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Country</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Verification</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Last Used</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {recipients?.map((recipient) => (
              <tr key={recipient?.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{recipient?.name}</p>
                      <p className="text-xs text-muted-foreground">{recipient?.relationship}</p>
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{recipient?.bankName}</p>
                    <p className="text-xs font-mono text-muted-foreground">{recipient?.iban}</p>
                    <p className="text-xs font-mono text-muted-foreground">{recipient?.swiftCode}</p>
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Image
                      src={recipient?.countryFlag}
                      alt={`${recipient?.country} flag`}
                      className="w-5 h-4 rounded-sm object-cover"
                    />
                    <span className="text-sm text-foreground">{recipient?.country}</span>
                  </div>
                  <div className="flex space-x-1 mt-1">
                    {recipient?.supportedCurrencies?.map((currency) => (
                      <span
                        key={currency}
                        className="px-1 py-0.5 text-xs bg-muted text-muted-foreground rounded"
                      >
                        {currency}
                      </span>
                    ))}
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <button
                    onClick={() => onViewDetails(recipient)}
                    className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${getVerificationStatusColor(recipient?.verificationStatus)}`}
                  >
                    <Icon name={getVerificationIcon(recipient?.verificationStatus)} size={12} />
                    <span className="capitalize">{recipient?.verificationStatus}</span>
                  </button>
                </td>
                
                <td className="px-6 py-4">
                  <p className="text-sm text-foreground">{formatLastUsed(recipient?.lastUsed)}</p>
                </td>
                
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => onSendMoney(recipient)}
                      iconName="Send"
                      iconSize={14}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(recipient)}
                      iconName="Edit"
                      iconSize={14}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(recipient)}
                      iconName="Trash2"
                      iconSize={14}
                      className="text-error hover:text-error"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecipientTable;